import type { BadgeProps } from '@/components/cargofl-ui/badge';
import type {
  AssessmentStatus,
  EmployeeStatus,
  Grade,
  GoalAssignmentStatus,
  OneOnOneState,
  WindowReason,
  DevConnectState,
} from '@cfl/shared';

type Variant = NonNullable<BadgeProps['variant']>;

/** Shared pill shape for status badges, matching the Frontline app's rounded pills. */
export const PILL_CLASS = 'rounded-full px-2.5 py-0.5 text-[11px] font-semibold';

/**
 * Assessment status -> soft pill.
 *
 * Only COMPLETED is green. Every "awaiting" state is blue rather than amber: a
 * quarter sitting at HR review is on track, not a warning, and colouring normal
 * progress as a problem is how a dashboard stops meaning anything. Lateness is a
 * separate signal - see `slaVariant`.
 */
export function assessmentStatusVariant(status: AssessmentStatus): Variant {
  switch (status) {
    case 'COMPLETED':
      return 'soft-success';
    case 'AWAITING_MANAGER':
    case 'AWAITING_EMPLOYEE_ACCEPTANCE':
    case 'AWAITING_HR':
    case 'AWAITING_MANAGEMENT':
      return 'info';
    case 'SELF_IN_PROGRESS':
      return 'soft-warning';
    case 'NOT_STARTED':
    default:
      return 'soft-muted';
  }
}

/**
 * The three exceptional states an assessment can be in, none of which is a stored
 * status - see the Assessment view fields `returnedAt`, `reopenActive` and `overdue`.
 *
 * All amber, including REOPENED, which HELPS the employee. That is the same rule the
 * HR window override follows: "somebody intervened by hand on this one record" is
 * exactly what the employee and every later reviewer should notice, and green would
 * hide it. OVERDUE is the one that is red - a window that shut with nothing in it is
 * not an exception being managed, it is a gap.
 */
export function assessmentExceptionVariant(state: 'returned' | 'reopened' | 'overdue'): Variant {
  return state === 'overdue' ? 'soft-destructive' : 'soft-warning';
}

export const ASSESSMENT_EXCEPTION_LABELS: Record<'returned' | 'reopened' | 'overdue', string> = {
  returned: 'Returned for correction',
  reopened: 'Reopened by HR',
  overdue: 'Overdue',
};

/** Overdue against the 3-day manager SLA, or due within a day. */
export function slaVariant(state: 'overdue' | 'due-soon' | 'on-track'): Variant {
  switch (state) {
    case 'overdue':
      return 'soft-destructive';
    case 'due-soon':
      return 'soft-warning';
    case 'on-track':
    default:
      return 'soft-muted';
  }
}

export function employeeStatusVariant(status: EmployeeStatus): Variant {
  switch (status) {
    case 'ACTIVE':
      return 'soft-success';
    case 'ACCEPTED':
      return 'info';
    case 'PENDING_ACCEPTANCE':
      return 'soft-warning';
    case 'ON_PIP':
      return 'soft-destructive';
    case 'EXITED':
    default:
      return 'soft-muted';
  }
}

/**
 * NOT_STARTED is not a stored status - it is the absence of an assignment row, and
 * the overview needs it visually distinct from DRAFT. A manager part-way through is
 * routine progress (info); a manager who has not begun is the one HR must chase.
 *
 * PENDING_ACCEPTANCE reads as live, not as a warning: acceptance was removed, so a set
 * left in it is not waiting on anybody - there is no longer anything to press. Colouring
 * it amber would send people looking for a button that does not exist.
 */
export function goalAssignmentVariant(status: GoalAssignmentStatus | 'NOT_STARTED'): Variant {
  switch (status) {
    case 'ACCEPTED':
    case 'PENDING_ACCEPTANCE':
      return 'soft-success';
    case 'DRAFT':
      return 'info';
    case 'NOT_STARTED':
    default:
      return 'soft-muted';
  }
}

/**
 * A band's standing against its target in a calibration panel.
 *
 * Over target is amber, not red: being 5 people over on A+ is the normal starting
 * point of a calibration and the thing the panel exists to work through. Under target
 * is muted rather than coloured at all - nobody needs chasing because a band came in
 * light, and colouring it would put a warning on two thirds of the screen.
 */
export function varianceVariant(delta: number): Variant {
  if (delta > 0) return 'soft-warning';
  if (delta < 0) return 'soft-muted';
  return 'soft-success';
}

/** Grade pill. C and D are the two that need to stand out in a calibration list. */
export function gradeVariant(grade: Grade): Variant {
  switch (grade) {
    case 'A+':
    case 'A':
      return 'soft-success';
    case 'B+':
    case 'B':
      return 'info';
    case 'C':
      return 'soft-warning';
    case 'D':
    default:
      return 'soft-destructive';
  }
}

/**
 * Why a self-assessment window is open or shut. A forced state is deliberately
 * amber even when it is open, because "HR opened this manually" is something the
 * employee and every later reviewer should notice rather than assume.
 */
export function windowReasonVariant(reason: WindowReason): Variant {
  switch (reason) {
    // Settled, not a warning: freezing is the intended end state of a quarter, and
    // amber here would read as "something went wrong" on every closed quarter.
    case 'FROZEN':
      return 'soft-muted';
    case 'OPEN_ON_CALENDAR':
      return 'soft-success';
    case 'FORCED_OPEN':
    case 'FORCED_CLOSED':
      return 'soft-warning';
    case 'BEFORE_WINDOW':
      return 'info';
    case 'AFTER_WINDOW':
    default:
      return 'soft-muted';
  }
}

export const WINDOW_REASON_LABELS: Record<WindowReason, string> = {
  FROZEN: 'Frozen by HR',
  OPEN_ON_CALENDAR: 'Open',
  FORCED_OPEN: 'Opened by HR',
  FORCED_CLOSED: 'Closed by HR',
  BEFORE_WINDOW: 'Not yet open',
  AFTER_WINDOW: 'Window passed',
};

/**
 * 1-on-1 state pill.
 *
 * Blue for SCHEDULED, following the same rule as the assessment statuses: a meeting
 * booked for next week is on track, and amber on it would put a warning on every
 * healthy row. The two amber states are the two where somebody is genuinely late,
 * and they are separate pills on purpose - AWAITING_NOTES is the manager's to clear
 * and AWAITING_ACKNOWLEDGEMENT is the employee's, so one label for both would name
 * the wrong person half the time.
 */
export function oneOnOneStateVariant(state: OneOnOneState): Variant {
  switch (state) {
    case 'ACKNOWLEDGED':
      return 'soft-success';
    // Muted, not red. A meeting that did not happen is a gap somebody has already
    // closed out with a reason - the record is settled, not on fire. Red here would
    // put an alarm on every legitimately cancelled conversation.
    case 'NOT_HELD':
      return 'soft-muted';
    case 'SCHEDULED':
      return 'info';
    case 'AWAITING_NOTES':
    case 'AWAITING_ACKNOWLEDGEMENT':
    default:
      return 'soft-warning';
  }
}

export const ONE_ON_ONE_STATE_LABELS: Record<OneOnOneState, string> = {
  SCHEDULED: 'Scheduled',
  AWAITING_NOTES: 'Awaiting notes',
  AWAITING_ACKNOWLEDGEMENT: 'Awaiting acknowledgement',
  ACKNOWLEDGED: 'Acknowledged',
  NOT_HELD: 'Not held',
};

/**
 * Dev Connect session state.
 *
 * Blue for a Friday still ahead, the same rule the assessment pills follow: an
 * upcoming session is routine progress and nothing is waiting on anybody. Amber is
 * kept for the one state that IS waiting on somebody - a Friday that has passed
 * with nobody recording what was shown, which blocks it entering the showcase log.
 */
export function devConnectStateVariant(state: DevConnectState): Variant {
  switch (state) {
    case 'HELD':
      return 'soft-success';
    case 'TODAY':
      return 'soft-success';
    case 'AWAITING_RECORD':
      return 'soft-warning';
    case 'UPCOMING':
    default:
      return 'info';
  }
}

export const DEV_CONNECT_STATE_LABELS: Record<DevConnectState, string> = {
  UPCOMING: 'Upcoming',
  TODAY: 'Today',
  AWAITING_RECORD: 'Awaiting write-up',
  HELD: 'Held',
};
